<script setup lang="ts">
import { computed, ref } from "vue";
import { UiButton, UiCard, UiChip, UiIcon, UiInputField, UiText, UiTextarea } from "@shopexperts/shared";
import type { BanRuleTestResult, TestBanRuleRequest } from "@shopexperts/legacy-api";
import { defaultTestBanRulesFormValues } from "@/app/composables/ban-list/banListForms";
import { formatRuleType } from "@/app/composables/ban-list/banListFormatters";

const props = defineProps<{
    isTesting?: boolean;
    result?: BanRuleTestResult | null;
}>();

const emit = defineEmits<{
    (e: "test", payload: TestBanRuleRequest): void;
    (e: "clear"): void;
}>();

const values = ref(defaultTestBanRulesFormValues());

const canTest = computed(() => Boolean(
    values.value.text.trim() || values.value.email.trim() || values.value.ip.trim(),
));

function runTest() {
    if (!canTest.value || props.isTesting) return;
    emit("test", {
        text: values.value.text.trim() || null,
        email: values.value.email.trim() || null,
        ip: values.value.ip.trim() || null,
    });
}

function reset() {
    values.value = defaultTestBanRulesFormValues();
    emit("clear");
}
</script>

<template>
    <UiCard padding="md">
        <div class="flex flex-col gap-4">
            <div class="flex flex-col gap-1">
                <div class="flex items-center gap-2">
                    <UiIcon name="preview" class="h-5 w-5 text-se-gray-600" />
                    <UiText as="p" variant="h6" class="font-semibold text-se-dark" text="Test a submission" />
                </div>
                <UiText
                    as="p"
                    variant="p-small"
                    class="text-se-gray-600"
                    text="Dry-runs the text against every active rule of every type. Nothing is banned, recorded, or counted — hits do not increment."
                />
            </div>

            <UiTextarea
                v-model="values.text"
                name="test_text"
                :rows="3"
                :disabled="props.isTesting"
                placeholder="We would like a guest post with a backlink."
                hint="The project title or description you want to screen."
            />

            <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <UiInputField
                    v-model="values.email"
                    name="test_email"
                    type="email"
                    :disabled="props.isTesting"
                    placeholder="submitter@example.com"
                    hint="Optional. Only types that screen an email use it."
                />
                <UiInputField
                    v-model="values.ip"
                    name="test_ip"
                    :disabled="props.isTesting"
                    placeholder="203.0.113.9"
                    hint="Optional. Only types that screen an IP use it."
                />
            </div>

            <div class="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                <UiButton
                    variant="outline"
                    label="Clear"
                    :disabled="props.isTesting"
                    @click="reset"
                />
                <UiButton
                    variant="primary"
                    :label="props.isTesting ? 'Testing...' : 'Run Test'"
                    :disabled="!canTest || props.isTesting"
                    @click="runTest"
                />
            </div>

            <div
                v-if="props.result"
                class="rounded-md border p-4"
                :class="props.result.matched
                    ? 'border-se-warn bg-se-warn-light'
                    : 'border-se-gray-200 bg-se-gray-50'"
            >
                <template v-if="props.result.matched && props.result.ban_rule">
                    <div class="flex flex-wrap items-center gap-2">
                        <UiIcon name="shield-tick" class="h-5 w-5 text-se-warn" />
                        <UiText
                            as="p"
                            variant="p"
                            class="font-semibold text-se-dark"
                            :text="`Blocked by “${props.result.ban_rule.name}”`"
                        />
                        <UiChip :text="formatRuleType(props.result.ban_rule.type)" size="sm" variant="outline" />
                    </div>

                    <div class="mt-3 flex flex-wrap items-center gap-2">
                        <UiText
                            as="span"
                            variant="caption-normal"
                            class="text-se-gray-600"
                            text="Matched terms:"
                        />
                        <UiChip
                            v-for="term in props.result.matched_terms"
                            :key="term"
                            :text="term"
                            size="sm"
                            rounded="full"
                            variant="neutral"
                        />
                    </div>

                    <div v-if="props.result.matched_text" class="mt-3">
                        <UiText
                            as="p"
                            variant="caption-normal"
                            class="text-se-gray-600"
                            text="Normalized text the matcher saw"
                        />
                        <p class="mt-1 rounded bg-white/70 p-2 font-mono text-xs break-words text-se-dark">
                            {{ props.result.matched_text }}
                        </p>
                    </div>
                </template>

                <div v-else class="flex items-center gap-2">
                    <UiIcon name="check-circle" class="h-5 w-5 text-se-gray-600" />
                    <UiText
                        as="p"
                        variant="p"
                        class="text-se-dark"
                        text="No rule fired — this submission would go through."
                    />
                </div>
            </div>
        </div>
    </UiCard>
</template>
